import { useState } from 'react';

type Step = 'input' | 'confirm' | 'complete';

export default function Home() {
  const [step, setStep] = useState<Step>('input');
  const [formData, setFormData] = useState({
    companyName: '',
    name: '',
    phone: '',
    email: '',
    inquiry: '',
    message: '',
    privacyAccepted: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // 入力 → 確認へ
  const handleConfirm = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.privacyAccepted) {
      setSubmitStatus('error');
      return;
    }

    setSubmitStatus('idle');
    setStep('confirm');
  };

  // 確認 → 送信
  const handleSubmitFinal = async () => {
    if (!formData.privacyAccepted) {
      setSubmitStatus('error');
      setStep('input');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('companyName', formData.companyName);
      formBody.append('name', formData.name);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('inquiry', formData.inquiry);
      formBody.append('message', formData.message);
      formBody.append('privacyAccepted', formData.privacyAccepted ? 'はい' : 'いいえ');

      // ★ここを send.php に変更（同一オリジン）
      const response = await fetch('/send.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setStep('complete');

        setFormData({
          companyName: '',
          name: '',
          phone: '',
          email: '',
          inquiry: '',
          message: '',
          privacyAccepted: false
        });
      } else {
        setSubmitStatus('error');
        setStep('input');
      }
    } catch {
      setSubmitStatus('error');
      setStep('input');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 表示用（確認画面でラベル整形したい場合）
  const inquiryLabel = formData.inquiry || '（未選択）';

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden" style={{ fontFamily: '"Inter", "Noto Sans JP", sans-serif' }}>
      {/* Floating Navigation */}
      <nav className="fixed top-6 left-1/2 -translate-x-1/2 z-50 bg-black/40 backdrop-blur-xl border border-cyan-500/30 rounded-full px-8 py-4 shadow-2xl shadow-cyan-500/20">
        <div className="flex items-center gap-10">
          <h1 className="text-2xl font-black bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent" style={{ fontFamily: '"Orbitron", sans-serif' }}>GM</h1>
          <div className="flex gap-8 items-center">
            <a href="#services" className="text-gray-300 hover:text-cyan-400 transition-all duration-300 font-medium cursor-pointer text-sm">サービス</a>
            <a href="#solutions" className="text-gray-300 hover:text-purple-400 transition-all duration-300 font-medium cursor-pointer text-sm">ソリューション</a>
            <a href="#tech" className="text-gray-300 hover:text-pink-400 transition-all duration-300 font-medium cursor-pointer text-sm">テクノロジー</a>
            <a href="#about" className="text-gray-300 hover:text-cyan-400 transition-all duration-300 font-medium cursor-pointer text-sm">会社情報</a>
            <a href="#contact" className="px-6 py-2 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 text-white font-bold rounded-full hover:shadow-2xl hover:shadow-purple-500/50 transition-all whitespace-nowrap cursor-pointer text-sm">
              お問い合わせ
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src="https://readdy.ai/api/search-image?query=Vibrant%20neon%20abstract%20digital%20art%20with%20glowing%20cyan%20purple%20pink%20lines%20flowing%20through%20dark%20space%2C%20futuristic%20technology%20background%20with%20electric%20neon%20circuits%20and%20data%20streams%2C%20cyberpunk%20style%20digital%20matrix%20with%20bright%20neon%20colors%2C%20high-tech%20abstract%20visualization%20with%20glowing%20geometric%20patterns%2C%20modern%20tech%20aesthetic%20with%20intense%20neon%20glow%20effects%2C%20digital%20innovation%20concept%20art%2C%20ultra-detailed%20neon%20lighting%2C%208k%20quality%20abstract%20technology%20art&width=1920&height=1080&seq=hero-neon-bg-001&orientation=landscape"
            alt=""
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black via-purple-900/20 to-black"></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-32 w-full relative z-10">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div>
              <div className="inline-block px-6 py-2 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 border border-cyan-500/50 rounded-full text-sm font-bold mb-8 text-cyan-400">
                ITコンサルティング
              </div>

              <h2 className="text-7xl font-black mb-8 leading-tight">
                <span className="bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                  未来を創る
                </span>
                <br />
                <span className="text-white">テクノロジー</span>
              </h2>

              <p className="text-xl text-gray-300 mb-12 leading-relaxed">
                株式会社GMは、最先端のITソリューションで<br />
                企業のデジタルトランスフォーメーションを加速。<br />
                戦略立案から実装まで、ワンストップで支援します。
              </p>

              <div className="flex gap-6 mb-16">
                <a href="#contact" className="px-10 py-5 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 text-white text-lg font-bold rounded-full hover:shadow-2xl hover:shadow-purple-500/50 transition-all whitespace-nowrap cursor-pointer">
                  無料相談
                </a>
                <a href="#services" className="px-10 py-5 bg-white/5 backdrop-blur-sm text-white text-lg font-bold rounded-full border-2 border-cyan-500/50 hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer">
                  詳しく見る
                </a>
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden border-2 border-purple-500/50 shadow-2xl shadow-purple-500/30">
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              </div>
              <div className="absolute -top-10 -right-10 w-60 h-60 bg-cyan-500 rounded-full opacity-20 blur-3xl animate-pulse"></div>
              <div className="absolute -bottom-10 -left-10 w-60 h-60 bg-purple-500 rounded-full opacity-20 blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-32 relative">
        <div className="absolute inset-0 z-0">
          <img
            src="https://readdy.ai/api/search-image?query=Dark%20abstract%20neon%20grid%20pattern%20with%20glowing%20cyan%20and%20purple%20lines%2C%20digital%20technology%20mesh%20background%20with%20neon%20circuit%20board%20design%2C%20futuristic%20geometric%20network%20with%20bright%20neon%20nodes%2C%20cyberpunk%20style%20tech%20grid%20with%20electric%20glow%2C%20modern%20abstract%20digital%20pattern%20with%20neon%20lighting%2C%20high-tech%20background%20with%20glowing%20connections%2C%20ultra-detailed%20neon%20grid%20art&width=1920&height=1200&seq=services-bg-neon-001&orientation=landscape"
            alt=""
            className="w-full h-full object-cover opacity-20"
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-20">
            <span className="text-cyan-400 font-bold text-sm tracking-widest uppercase mb-4 block">Services</span>
            <h3 className="text-6xl font-black mb-6">
              <span className="bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                提供サービス
              </span>
            </h3>
            <p className="text-xl text-gray-400">
              最先端のテクノロジーで、ビジネスの可能性を最大化
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Service 1 */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500 to-purple-500 rounded-3xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-500"></div>
              <div className="relative bg-black/40 backdrop-blur-xl border border-cyan-500/30 rounded-3xl p-8 hover:border-cyan-500 transition-all duration-500 h-full">
                <div className="mb-6 rounded-2xl overflow-hidden border border-cyan-500/30">
                  <img
                    src="https://readdy.ai/api/search-image?query=Neon%20digital%20transformation%20concept%20with%20glowing%20data%20streams%20and%20cloud%20computing%20visualization%2C%20futuristic%20business%20technology%20with%20bright%20cyan%20and%20purple%20neon%20lights%2C%20modern%20IT%20infrastructure%20with%20neon%20circuit%20patterns%2C%20digital%20innovation%20and%20cloud%20services%20with%20electric%20glow%20effects%2C%20high-tech%20enterprise%20solutions%20with%20neon%20aesthetic%2C%20cyberpunk%20style%20business%20technology%20art%2C%20ultra-detailed%20neon%20tech%20visualization&width=400&height=250&seq=service-dx-neon-001&orientation=landscape"
                    alt="DXコンサルティング"
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>

                <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-cyan-500/50">
                  <i className="ri-cloud-line text-3xl text-white"></i>
                </div>

                <h4 className="text-2xl font-black text-white mb-4">DXコンサルティング</h4>

                <p className="text-gray-400 leading-relaxed mb-6">
                  デジタルトランスフォーメーションの戦略立案から実行まで。クラウド移行、業務自動化、データ活用で企業の競争力を強化します。
                </p>

                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-gray-300">
                    <div className="w-2 h-2 bg-cyan-400 rounded-full flex-shrink-0"></div>
                    <span className="text-sm">DX戦略立案・ロードマップ作成</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <div className="w-2 h-2 bg-cyan-400 rounded-full flex-shrink-0"></div>
                    <span className="text-sm">クラウド移行・最適化</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <div className="w-2 h-2 bg-cyan-400 rounded-full flex-shrink-0"></div>
                    <span className="text-sm">業務プロセス自動化</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Service 2 */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-pink-500 rounded-3xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-500"></div>
              <div className="relative bg-black/40 backdrop-blur-xl border border-purple-500/30 rounded-3xl p-8 hover:border-purple-500 transition-all duration-500 h-full">
                <div className="mb-6 rounded-2xl overflow-hidden border border-purple-500/30">
                  <img
                    src="https://readdy.ai/api/search-image?query=Neon%20AI%20and%20machine%20learning%20visualization%20with%20glowing%20neural%20networks%2C%20futuristic%20artificial%20intelligence%20with%20bright%20purple%20and%20pink%20neon%20effects%2C%20advanced%20data%20science%20and%20analytics%20with%20electric%20neon%20glow%2C%20cyberpunk%20style%20AI%20brain%20visualization%20with%20neon%20circuits%2C%20modern%20machine%20learning%20concept%20with%20neon%20lighting%2C%20high-tech%20AI%20technology%20art%20with%20vibrant%20neon%20colors%2C%20ultra-detailed%20neon%20AI%20illustration&width=400&height=250&seq=service-ai-neon-001&orientation=landscape"
                    alt="AI・データ分析"
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>

                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-purple-500/50">
                  <i className="ri-brain-line text-3xl text-white"></i>
                </div>

                <h4 className="text-2xl font-black text-white mb-4">AI・データ分析</h4>

                <p className="text-gray-400 leading-relaxed mb-6">
                  AIと機械学習を活用したデータ分析で、ビジネスインサイトを創出。予測分析、異常検知、自動化で意思決定を支援します。
                </p>

                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-gray-300">
                    <div className="w-2 h-2 bg-purple-400 rounded-full flex-shrink-0"></div>
                    <span className="text-sm">機械学習モデル開発</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <div className="w-2 h-2 bg-purple-400 rounded-full flex-shrink-0"></div>
                    <span className="text-sm">ビッグデータ分析基盤構築</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <div className="w-2 h-2 bg-purple-400 rounded-full flex-shrink-0"></div>
                    <span className="text-sm">予測分析・異常検知</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Service 3 */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-pink-500 to-cyan-500 rounded-3xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-500"></div>
              <div className="relative bg-black/40 backdrop-blur-xl border border-pink-500/30 rounded-3xl p-8 hover:border-pink-500 transition-all duration-500 h-full">
                <div className="mb-6 rounded-2xl overflow-hidden border border-pink-500/30">
                  <img
                    src="https://readdy.ai/api/search-image?query=Neon%20cybersecurity%20shield%20with%20glowing%20protection%20barriers%2C%20futuristic%20security%20system%20with%20bright%20cyan%20and%20pink%20neon%20lights%2C%20digital%20security%20network%20with%20electric%20neon%20circuits%2C%20modern%20cyber%20defense%20visualization%20with%20neon%20glow%20effects%2C%20high-tech%20security%20infrastructure%20with%20neon%20aesthetic%2C%20cyberpunk%20style%20security%20concept%20art%20with%20vibrant%20neon%2C%20ultra-detailed%20neon%20security%20illustration&width=400&height=250&seq=service-security-neon-001&orientation=landscape"
                    alt="セキュリティ対策"
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>

                <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-pink-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-pink-500/50">
                  <i className="ri-shield-check-line text-3xl text-white"></i>
                </div>

                <h4 className="text-2xl font-black text-white mb-4">セキュリティ対策</h4>

                <p className="text-gray-400 leading-relaxed mb-6">
                  最新のサイバーセキュリティ対策で、企業の情報資産を保護。脆弱性診断、セキュリティ監視、インシデント対応まで包括的に支援。
                </p>

                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-gray-300">
                    <div className="w-2 h-2 bg-pink-400 rounded-full flex-shrink-0"></div>
                    <span className="text-sm">セキュリティ診断・監査</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <div className="w-2 h-2 bg-pink-400 rounded-full flex-shrink-0"></div>
                    <span className="text-sm">24/7セキュリティ監視</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <div className="w-2 h-2 bg-pink-400 rounded-full flex-shrink-0"></div>
                    <span className="text-sm">インシデント対応・復旧</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Solutions */}
      <section id="solutions" className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20neon%20waves%20flowing%20diagonally%20with%20cyan%20purple%20pink%20gradients%2C%20futuristic%20liquid%20neon%20patterns%20with%20smooth%20flowing%20lines%2C%20dynamic%20neon%20energy%20streams%20in%20dark%20space%2C%20modern%20abstract%20neon%20art%20with%20fluid%20motion%2C%20cyberpunk%20style%20flowing%20neon%20background%2C%20high-tech%20abstract%20visualization%20with%20glowing%20waves%2C%20ultra-detailed%20neon%20flow%20art&width=1920&height=1200&seq=solutions-bg-neon-001&orientation=landscape"
            alt=""
            className="w-full h-full object-cover opacity-30"
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-20">
            <span className="text-purple-400 font-bold text-sm tracking-widest uppercase mb-4 block">Solutions</span>
            <h3 className="text-6xl font-black mb-6">
              <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">
                業界別ソリューション
              </span>
            </h3>
          </div>

          {/* ここ以下、あなたの元コードのまま（Solution 1〜3） */}
          <div className="space-y-32">
            {/* Solution 1 */}
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500 to-purple-500 rounded-3xl opacity-20 blur-3xl"></div>
                <div className="relative rounded-3xl overflow-hidden border-2 border-cyan-500/50 shadow-2xl shadow-cyan-500/30">
                  <img
                    src="https://readdy.ai/api/search-image?query=Neon%20financial%20technology%20dashboard%20with%20glowing%20charts%20and%20data%2C%20futuristic%20fintech%20interface%20with%20bright%20cyan%20and%20purple%20neon%20displays%2C%20modern%20banking%20and%20finance%20visualization%20with%20electric%20neon%20effects%2C%20cyberpunk%20style%20financial%20analytics%20with%20neon%20lighting%2C%20high-tech%20trading%20platform%20with%20neon%20aesthetic%2C%20digital%20finance%20concept%20art%20with%20vibrant%20neon%20colors%2C%20ultra-detailed%20neon%20fintech%20illustration&width=600&height=400&seq=solution-finance-neon-001&orientation=landscape"
                    alt="金融業界向け"
                    className="w-full h-96 object-cover"
                  />
                </div>
              </div>
              <div>
                <div className="inline-block px-5 py-2 bg-cyan-500/20 border border-cyan-500/50 rounded-full text-sm font-bold mb-6 text-cyan-400">
                  Finance
                </div>
                <h4 className="text-5xl font-black text-white mb-6">金融業界向けソリューション</h4>
                <p className="text-lg text-gray-300 leading-relaxed mb-8">
                  フィンテック、デジタルバンキング、リスク管理システムなど、金融業界特有の課題に対応。規制対応とイノベーションを両立させます。
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-cyan-500/20 border border-cyan-500/50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-cyan-400"></i>
                    </div>
                    <span className="text-gray-300">デジタルバンキングプラットフォーム構築</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-cyan-500/20 border border-cyan-500/50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-cyan-400"></i>
                    </div>
                    <span className="text-gray-300">AIによる不正検知システム</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-cyan-500/20 border border-cyan-500/50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-cyan-400"></i>
                    </div>
                    <span className="text-gray-300">ブロックチェーン技術活用</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Solution 2 */}
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div className="order-2 md:order-1">
                <div className="inline-block px-5 py-2 bg-purple-500/20 border border-purple-500/50 rounded-full text-sm font-bold mb-6 text-purple-400">
                  Retail
                </div>
                <h4 className="text-5xl font-black text-white mb-6">小売・EC業界向け</h4>
                <p className="text-lg text-gray-300 leading-relaxed mb-8">
                  オムニチャネル戦略、在庫最適化、パーソナライゼーションなど、顧客体験を向上させるデジタルソリューションを提供します。
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-purple-500/20 border border-purple-500/50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-purple-400"></i>
                    </div>
                    <span className="text-gray-300">ECプラットフォーム構築・最適化</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-purple-500/20 border border-purple-500/50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-purple-400"></i>
                    </div>
                    <span className="text-gray-300">AIレコメンデーションエンジン</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-purple-500/20 border border-purple-500/50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-purple-400"></i>
                    </div>
                    <span className="text-gray-300">在庫管理・需要予測システム</span>
                  </div>
                </div>
              </div>
              <div className="order-1 md:order-2 relative">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-pink-500 rounded-3xl opacity-20 blur-3xl"></div>
                <div className="relative rounded-3xl overflow-hidden border-2 border-purple-500/50 shadow-2xl shadow-purple-500/30">
                  <img
                    src="https://readdy.ai/api/search-image?query=Neon%20e-commerce%20and%20retail%20technology%20with%20glowing%20shopping%20interfaces%2C%20futuristic%20online%20shopping%20platform%20with%20bright%20purple%20and%20pink%20neon%20lights%2C%20modern%20retail%20analytics%20dashboard%20with%20electric%20neon%20effects%2C%20cyberpunk%20style%20digital%20commerce%20with%20neon%20aesthetic%2C%20high-tech%20retail%20solutions%20with%20neon%20glow%2C%20omnichannel%20retail%20visualization%20with%20vibrant%20neon%2C%20ultra-detailed%20neon%20retail%20tech%20art&width=600&height=400&seq=solution-retail-neon-001&orientation=landscape"
                    alt="小売・EC業界向け"
                    className="w-full h-96 object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Solution 3 */}
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-pink-500 to-cyan-500 rounded-3xl opacity-20 blur-3xl"></div>
                <div className="relative rounded-3xl overflow-hidden border-2 border-pink-500/50 shadow-2xl shadow-pink-500/30">
                  <img
                    src="https://readdy.ai/api/search-image?query=Neon%20manufacturing%20and%20IoT%20technology%20with%20glowing%20factory%20automation%2C%20futuristic%20smart%20factory%20with%20bright%20cyan%20and%20pink%20neon%20lights%2C%20modern%20industrial%20IoT%20sensors%20with%20electric%20neon%20effects%2C%20cyberpunk%20style%20manufacturing%20systems%20with%20neon%20aesthetic%2C%20high-tech%20production%20line%20with%20neon%20glow%2C%20Industry%204.0%20visualization%20with%20vibrant%20neon%20colors%2C%20ultra-detailed%20neon%20industrial%20tech%20art&width=600&height=400&seq=solution-manufacturing-neon-001&orientation=landscape"
                    alt="製造業向け"
                    className="w-full h-96 object-cover"
                  />
                </div>
              </div>
              <div>
                <div className="inline-block px-5 py-2 bg-pink-500/20 border border-pink-500/50 rounded-full text-sm font-bold mb-6 text-pink-400">
                  Manufacturing
                </div>
                <h4 className="text-5xl font-black text-white mb-6">製造業向けソリューション</h4>
                <p className="text-lg text-gray-300 leading-relaxed mb-8">
                  スマートファクトリー、IoT活用、予知保全など、製造現場のデジタル化を推進。生産性向上とコスト削減を実現します。
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-pink-500/20 border border-pink-500/50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-pink-400"></i>
                    </div>
                    <span className="text-gray-300">IoTセンサー・データ収集基盤</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-pink-500/20 border border-pink-500/50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-pink-400"></i>
                    </div>
                    <span className="text-gray-300">AI予知保全システム</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-pink-500/20 border border-pink-500/50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-pink-400"></i>
                    </div>
                    <span className="text-gray-300">生産管理システム最適化</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Tech */}
      <section id="tech" className="py-32 relative">
        <div className="absolute inset-0 z-0">
          <img
            src="https://readdy.ai/api/search-image?query=Neon%20hexagonal%20tech%20pattern%20with%20glowing%20nodes%20and%20connections%2C%20futuristic%20honeycomb%20grid%20with%20bright%20cyan%20purple%20pink%20neon%20lights%2C%20digital%20technology%20mesh%20with%20electric%20hexagon%20shapes%2C%20cyberpunk%20style%20geometric%20network%20with%20neon%20glow%2C%20modern%20abstract%20tech%20pattern%20with%20glowing%20hexagons%2C%20high-tech%20background%20with%20neon%20lighting%20effects%2C%20ultra-detailed%20neon%20hexagon%20art&width=1920&height=1200&seq=tech-bg-neon-001&orientation=landscape"
            alt=""
            className="w-full h-full object-cover opacity-20"
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-20">
            <span className="text-pink-400 font-bold text-sm tracking-widest uppercase mb-4 block">Technology</span>
            <h3 className="text-6xl font-black mb-6">
              <span className="bg-gradient-to-r from-pink-400 via-cyan-400 to-purple-400 bg-clip-text text-transparent">
                テクノロジースタック
              </span>
            </h3>
            <p className="text-xl text-gray-400">
              最先端の技術で、最高のソリューションを
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            <div className="group relative bg-black/40 backdrop-blur-xl border border-cyan-500/30 rounded-2xl p-8 hover:border-cyan-500 transition-all duration-300 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-cyan-500/50 group-hover:scale-110 transition-transform">
                <i className="ri-cloud-line text-4xl text-white"></i>
              </div>
              <h4 className="text-xl font-black text-white mb-3">クラウド</h4>
              <p className="text-sm text-gray-400">AWS / Azure / GCP</p>
            </div>

            <div className="group relative bg-black/40 backdrop-blur-xl border border-purple-500/30 rounded-2xl p-8 hover:border-purple-500 transition-all duration-300 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-purple-500/50 group-hover:scale-110 transition-transform">
                <i className="ri-database-2-line text-4xl text-white"></i>
              </div>
              <h4 className="text-xl font-black text-white mb-3">データベース</h4>
              <p className="text-sm text-gray-400">PostgreSQL / MongoDB / Redis</p>
            </div>

            <div className="group relative bg-black/40 backdrop-blur-xl border border-pink-500/30 rounded-2xl p-8 hover:border-pink-500 transition-all duration-300 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-pink-500 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-pink-500/50 group-hover:scale-110 transition-transform">
                <i className="ri-code-s-slash-line text-4xl text-white"></i>
              </div>
              <h4 className="text-xl font-black text-white mb-3">開発言語</h4>
              <p className="text-sm text-gray-400">Python / TypeScript / Go</p>
            </div>

            <div className="group relative bg-black/40 backdrop-blur-xl border border-cyan-500/30 rounded-2xl p-8 hover:border-cyan-500 transition-all duration-300 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-cyan-500 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-cyan-500/50 group-hover:scale-110 transition-transform">
                <i className="ri-robot-line text-4xl text-white"></i>
              </div>
              <h4 className="text-xl font-black text-white mb-3">AI/ML</h4>
              <p className="text-sm text-gray-400">TensorFlow / PyTorch / OpenAI</p>
            </div>

            <div className="group relative bg-black/40 backdrop-blur-xl border border-purple-500/30 rounded-2xl p-8 hover:border-purple-500 transition-all duration-300 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-purple-500/50 group-hover:scale-110 transition-transform">
                <i className="ri-container-line text-4xl text-white"></i>
              </div>
              <h4 className="text-xl font-black text-white mb-3">コンテナ</h4>
              <p className="text-sm text-gray-400">Docker / Kubernetes</p>
            </div>

            <div className="group relative bg-black/40 backdrop-blur-xl border border-pink-500/30 rounded-2xl p-8 hover:border-pink-500 transition-all duration-300 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-pink-500 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-pink-500/50 group-hover:scale-110 transition-transform">
                <i className="ri-git-branch-line text-4xl text-white"></i>
              </div>
              <h4 className="text-xl font-black text-white mb-3">CI/CD</h4>
              <p className="text-sm text-gray-400">GitHub Actions / Jenkins</p>
            </div>

            <div className="group relative bg-black/40 backdrop-blur-xl border border-cyan-500/30 rounded-2xl p-8 hover:border-cyan-500 transition-all duration-300 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-cyan-500/50 group-hover:scale-110 transition-transform">
                <i className="ri-line-chart-line text-4xl text-white"></i>
              </div>
              <h4 className="text-xl font-black text-white mb-3">分析</h4>
              <p className="text-sm text-gray-400">Tableau / Power BI / Looker</p>
            </div>

            <div className="group relative bg-black/40 backdrop-blur-xl border border-purple-500/30 rounded-2xl p-8 hover:border-purple-500 transition-all duration-300 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-purple-500/50 group-hover:scale-110 transition-transform">
                <i className="ri-shield-check-line text-4xl text-white"></i>
              </div>
              <h4 className="text-xl font-black text-white mb-3">セキュリティ</h4>
              <p className="text-sm text-gray-400">Zero Trust / SIEM / WAF</p>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-32 relative">
        <div className="absolute inset-0 z-0 bg-gradient-to-b from-black via-purple-900/10 to-black"></div>

        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="text-center mb-20">
            <span className="text-cyan-400 font-bold text-sm tracking-widest uppercase mb-4 block">Process</span>
            <h3 className="text-6xl font-black mb-6">
              <span className="bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                プロジェクトの流れ
              </span>
            </h3>
          </div>

          <div className="relative">
            <div className="hidden md:block absolute top-32 left-0 right-0 h-1 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 opacity-30"></div>

            <div className="grid md:grid-cols-5 gap-6 relative">
              <div className="text-center">
                <div className="relative mb-6">
                  <div className="w-40 h-40 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-3xl flex items-center justify-center mx-auto shadow-2xl shadow-cyan-500/50 relative z-10 border-4 border-black">
                    <i className="ri-chat-3-line text-6xl text-white"></i>
                  </div>
                  <div className="absolute -top-3 -right-3 w-12 h-12 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-full flex items-center justify-center text-white font-black text-xl shadow-lg">
                    1
                  </div>
                </div>
                <h4 className="text-xl font-black text-white mb-2">ヒアリング</h4>
                <p className="text-sm text-gray-400">課題の把握</p>
              </div>

              <div className="text-center">
                <div className="relative mb-6">
                  <div className="w-40 h-40 bg-gradient-to-br from-purple-500 to-purple-600 rounded-3xl flex items-center justify-center mx-auto shadow-2xl shadow-purple-500/50 relative z-10 border-4 border-black">
                    <i className="ri-search-line text-6xl text-white"></i>
                  </div>
                  <div className="absolute -top-3 -right-3 w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center text-white font-black text-xl shadow-lg">
                    2
                  </div>
                </div>
                <h4 className="text-xl font-black text-white mb-2">分析</h4>
                <p className="text-sm text-gray-400">現状分析</p>
              </div>

              <div className="text-center">
                <div className="relative mb-6">
                  <div className="w-40 h-40 bg-gradient-to-br from-pink-500 to-pink-600 rounded-3xl flex items-center justify-center mx-auto shadow-2xl shadow-pink-500/50 relative z-10 border-4 border-black">
                    <i className="ri-lightbulb-line text-6xl text-white"></i>
                  </div>
                  <div className="absolute -top-3 -right-3 w-12 h-12 bg-gradient-to-br from-pink-400 to-pink-600 rounded-full flex items-center justify-center text-white font-black text-xl shadow-lg">
                    3
                  </div>
                </div>
                <h4 className="text-xl font-black text-white mb-2">提案</h4>
                <p className="text-sm text-gray-400">最適な戦略</p>
              </div>

              <div className="text-center">
                <div className="relative mb-6">
                  <div className="w-40 h-40 bg-gradient-to-br from-cyan-500 to-purple-500 rounded-3xl flex items-center justify-center mx-auto shadow-2xl shadow-cyan-500/50 relative z-10 border-4 border-black">
                    <i className="ri-tools-line text-6xl text-white"></i>
                  </div>
                  <div className="absolute -top-3 -right-3 w-12 h-12 bg-gradient-to-br from-cyan-400 to-purple-600 rounded-full flex items-center justify-center text-white font-black text-xl shadow-lg">
                    4
                  </div>
                </div>
                <h4 className="text-xl font-black text-white mb-2">実装</h4>
                <p className="text-sm text-gray-400">開発・導入</p>
              </div>

              <div className="text-center">
                <div className="relative mb-6">
                  <div className="w-40 h-40 bg-gradient-to-br from-purple-500 to-pink-500 rounded-3xl flex items-center justify-center mx-auto shadow-2xl shadow-purple-500/50 relative z-10 border-4 border-black">
                    <i className="ri-customer-service-2-line text-6xl text-white"></i>
                  </div>
                  <div className="absolute -top-3 -right-3 w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-600 rounded-full flex items-center justify-center text-white font-black text-xl shadow-lg">
                    5
                  </div>
                </div>
                <h4 className="text-xl font-black text-white mb-2">サポート</h4>
                <p className="text-sm text-gray-400">運用支援</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section（★ここが確認画面対応） */}
      <section id="contact" className="py-32 relative">
        <div className="absolute inset-0 z-0">
          <img
            src="https://readdy.ai/api/search-image?query=Neon%20contact%20and%20communication%20concept%20with%20glowing%20message%20icons%2C%20futuristic%20digital%20communication%20with%20bright%20cyan%20and%20purple%20neon%20effects%2C%20modern%20contact%20interface%20with%20electric%20neon%20glow%2C%20cyberpunk%20style%20messaging%20system%20with%20neon%20aesthetic%2C%20high-tech%20communication%20visualization%20with%20neon%20lighting%2C%20ultra-detailed%20neon%20contact%20art&width=1920&height=1200&seq=contact-bg-neon-001&orientation=landscape"
            alt=""
            className="w-full h-full object-cover opacity-20"
          />
        </div>

        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <span className="text-cyan-400 font-bold text-sm tracking-widest uppercase mb-4 block">Contact</span>
            <h3 className="text-6xl font-black mb-6">
              <span className="bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                お問い合わせ
              </span>
            </h3>
            <p className="text-xl text-gray-400">
              ITコンサルティングに関するご相談は、お気軽にどうぞ
            </p>
          </div>

          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500 via-purple-500 to-pink-500 rounded-3xl opacity-10 blur-3xl"></div>
            <div className="relative bg-black/40 backdrop-blur-xl border border-cyan-500/30 rounded-3xl p-12">

              {/* ===== 入力画面 ===== */}
              {step === 'input' && (
                <form onSubmit={handleConfirm} id="gm-contact-form">
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-bold text-gray-300 mb-3">会社名 <span className="text-cyan-400">*</span></label>
                      <input
                        type="text"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        required
                        className="w-full px-5 py-4 bg-black/50 border-2 border-cyan-500/30 rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all text-sm"
                        placeholder="株式会社〇〇"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-300 mb-3">お名前 <span className="text-cyan-400">*</span></label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-5 py-4 bg-black/50 border-2 border-cyan-500/30 rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all text-sm"
                        placeholder="山田 太郎"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-bold text-gray-300 mb-3">電話番号 <span className="text-cyan-400">*</span></label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-5 py-4 bg-black/50 border-2 border-cyan-500/30 rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all text-sm"
                        placeholder="03-1234-5678"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-300 mb-3">メールアドレス <span className="text-cyan-400">*</span></label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-5 py-4 bg-black/50 border-2 border-cyan-500/30 rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all text-sm"
                        placeholder="example@company.com"
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-bold text-gray-300 mb-3">お問い合わせ内容 <span className="text-cyan-400">*</span></label>
                    <select
                      name="inquiry"
                      value={formData.inquiry}
                      onChange={handleChange}
                      required
                      className="w-full px-5 py-4 pr-8 bg-black/50 border-2 border-cyan-500/30 rounded-2xl text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all text-sm cursor-pointer appearance-none"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2322d3ee'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 1rem center',
                        backgroundSize: '1.5em 1.5em'
                      }}
                    >
                      <option value="" className="bg-black">選択してください</option>
                      <option value="DXコンサルティングについて" className="bg-black">DXコンサルティングについて</option>
                      <option value="AI・データ分析について" className="bg-black">AI・データ分析について</option>
                      <option value="セキュリティ対策について" className="bg-black">セキュリティ対策について</option>
                      <option value="料金について" className="bg-black">料金について</option>
                      <option value="その他" className="bg-black">その他</option>
                    </select>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-bold text-gray-300 mb-3">詳細・ご質問</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      maxLength={500}
                      rows={5}
                      className="w-full px-5 py-4 bg-black/50 border-2 border-cyan-500/30 rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all resize-none text-sm"
                      placeholder="ご質問やご要望をご記入ください（500文字以内）"
                    ></textarea>
                    <div className="text-right text-sm text-gray-500 mt-2">{formData.message.length}/500</div>
                  </div>

                  <div className="mb-8">
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        name="privacyAccepted"
                        checked={formData.privacyAccepted}
                        onChange={handleChange}
                        required
                        className="mt-1 w-5 h-5 rounded border-cyan-500/50 bg-black/50 text-cyan-500 focus:ring-2 focus:ring-cyan-500 cursor-pointer"
                      />
                      <span className="text-sm text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors">
                        <a href="/privacy" className="text-cyan-400 hover:text-cyan-300 underline">プライバシーポリシー</a>に同意します <span className="text-cyan-400">*</span>
                      </span>
                    </label>
                  </div>

                  {submitStatus === 'error' && (
                    <div className="mb-6 p-4 bg-pink-500/20 border-2 border-pink-500/50 rounded-2xl text-pink-300 text-sm">
                      <i className="ri-error-warning-line mr-2"></i>
                      プライバシーポリシーへの同意をご確認の上、もう一度お試しください。
                    </div>
                  )}

                  <button
                    type="submit"
                    className="w-full py-5 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 text-white text-lg font-bold rounded-full hover:shadow-2xl hover:shadow-purple-500/50 transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer"
                  >
                    確認画面へ
                  </button>
                </form>
              )}

              {/* ===== 確認画面 ===== */}
              {step === 'confirm' && (
                <div>
                  <div className="mb-8 p-5 bg-white/5 border border-cyan-500/30 rounded-2xl">
                    <div className="text-sm text-gray-400 mb-4">以下の内容で送信します。よろしければ「送信する」を押してください。</div>

                    <div className="space-y-4 text-sm">
                      <div className="flex justify-between gap-6 border-b border-white/10 pb-3">
                        <div className="text-gray-400">会社名</div>
                        <div className="text-white font-bold text-right">{formData.companyName}</div>
                      </div>
                      <div className="flex justify-between gap-6 border-b border-white/10 pb-3">
                        <div className="text-gray-400">お名前</div>
                        <div className="text-white font-bold text-right">{formData.name}</div>
                      </div>
                      <div className="flex justify-between gap-6 border-b border-white/10 pb-3">
                        <div className="text-gray-400">電話番号</div>
                        <div className="text-white font-bold text-right">{formData.phone}</div>
                      </div>
                      <div className="flex justify-between gap-6 border-b border-white/10 pb-3">
                        <div className="text-gray-400">メールアドレス</div>
                        <div className="text-white font-bold text-right">{formData.email}</div>
                      </div>
                      <div className="flex justify-between gap-6 border-b border-white/10 pb-3">
                        <div className="text-gray-400">お問い合わせ内容</div>
                        <div className="text-white font-bold text-right">{inquiryLabel}</div>
                      </div>
                      <div className="border-b border-white/10 pb-3">
                        <div className="text-gray-400 mb-2">詳細・ご質問</div>
                        <div className="text-white whitespace-pre-wrap">{formData.message || '（未入力）'}</div>
                      </div>
                      <div className="text-gray-400 text-xs">
                        プライバシーポリシー同意：{formData.privacyAccepted ? 'はい' : 'いいえ'}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={() => setStep('input')}
                      className="flex-1 py-4 bg-white/5 backdrop-blur-sm text-white font-bold rounded-full border-2 border-cyan-500/50 hover:bg-white/10 transition-all cursor-pointer"
                      disabled={isSubmitting}
                    >
                      修正する
                    </button>

                    <button
                      type="button"
                      onClick={handleSubmitFinal}
                      disabled={isSubmitting}
                      className="flex-1 py-4 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 text-white font-bold rounded-full hover:shadow-2xl hover:shadow-purple-500/50 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? '送信中...' : '送信する'}
                    </button>
                  </div>
                </div>
              )}

              {/* ===== 完了画面 ===== */}
              {step === 'complete' && (
                <div className="text-center">
                  <div className="mb-6 p-6 bg-cyan-500/20 border-2 border-cyan-500/50 rounded-2xl text-cyan-300">
                    <i className="ri-checkbox-circle-line mr-2"></i>
                    お問い合わせを受け付けました。担当者より折り返しご連絡いたします。
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setStep('input');
                      setSubmitStatus('idle');
                    }}
                    className="px-10 py-4 bg-white/5 backdrop-blur-sm text-white font-bold rounded-full border-2 border-cyan-500/50 hover:bg-white/10 transition-all cursor-pointer"
                  >
                    もう一度送る
                  </button>
                </div>
              )}

            </div>
          </div>

          {/* Contact Info */}
          <div className="grid md:grid-cols-2 gap-6 mt-12">
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500 to-purple-500 rounded-3xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-500"></div>
              <div className="relative bg-black/40 backdrop-blur-xl border border-cyan-500/30 rounded-3xl p-8 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-2xl flex items-center justify-center mb-4 mx-auto shadow-lg shadow-cyan-500/50">
                  <i className="ri-phone-line text-2xl text-white"></i>
                </div>
                <h4 className="text-xl font-black text-white mb-2">お電話でのお問い合わせ</h4>
                <p className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent mb-2">080-8928-9892</p>
                <p className="text-sm text-gray-400">受付時間：平日 9:00〜18:00</p>
              </div>
            </div>

            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-pink-500 rounded-3xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-500"></div>
              <div className="relative bg-black/40 backdrop-blur-xl border border-purple-500/30 rounded-3xl p-8 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-4 mx-auto shadow-lg shadow-purple-500/50">
                  <i className="ri-mail-line text-2xl text-white"></i>
                </div>
                <h4 className="text-xl font-black text-white mb-2">メールでのお問い合わせ</h4>
                <p className="text-lg font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-2">info.gm@gmsys-inc.com</p>
                <p className="text-sm text-gray-400">24時間受付</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Info */}
      <section id="about" className="py-32 relative">
        <div className="absolute inset-0 z-0 bg-gradient-to-b from-black via-cyan-900/10 to-black"></div>

        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500 via-purple-500 to-pink-500 rounded-3xl opacity-10 blur-3xl"></div>
            <div className="relative bg-black/40 backdrop-blur-xl border border-cyan-500/30 rounded-3xl p-12">
              <h3 className="text-5xl font-black text-center mb-12">
                <span className="bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                  会社情報
                </span>
              </h3>
              <div className="space-y-8">
                <div className="flex gap-6 items-start">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-2xl flex items-center justify-center shadow-lg shadow-cyan-500/50">
                      <i className="ri-building-line text-2xl text-white"></i>
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400 mb-2 font-medium">会社名</div>
                    <div className="text-2xl font-bold text-white">株式会社GM</div>
                  </div>
                </div>
                <div className="flex gap-6 items-start">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-500/50">
                      <i className="ri-calendar-line text-2xl text-white"></i>
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400 mb-2 font-medium">設立</div>
                    <div className="text-2xl font-bold text-white">令和6年11月5日</div>
                  </div>
                </div>
                <div className="flex gap-6 items-start">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-pink-600 rounded-2xl flex items-center justify-center shadow-lg shadow-pink-500/50">
                      <i className="ri-map-pin-line text-2xl text-white"></i>
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400 mb-2 font-medium">所在地</div>
                    <div className="text-xl font-bold text-white">東京都北区王子本町二丁目14番14号ROWHOUSE王子本町20 3号</div>
                  </div>
                </div>
                <div className="flex gap-6 items-start">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-purple-500 rounded-2xl flex items-center justify-center shadow-lg shadow-cyan-500/50">
                      <i className="ri-briefcase-line text-2xl text-white"></i>
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400 mb-2 font-medium">事業内容</div>
                    <div className="text-lg text-gray-300">
                      <div className="font-bold text-white mb-3">ITに関するコンサルティング</div>
                      <div className="space-y-2">
                        <div className="flex items-start gap-3">
                          <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 flex-shrink-0"></div>
                          <span>デジタルトランスフォーメーション支援</span>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="w-2 h-2 bg-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                          <span>AI・データ分析ソリューション</span>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="w-2 h-2 bg-pink-400 rounded-full mt-2 flex-shrink-0"></div>
                          <span>セキュリティコンサルティング</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-cyan-500/20 py-12 relative">
        <div className="absolute inset-0 z-0">
          <img
            src="https://readdy.ai/api/search-image?query=Subtle%20neon%20grid%20pattern%20for%20footer%20background%2C%20dark%20abstract%20tech%20texture%20with%20faint%20cyan%20purple%20pink%20glow%2C%20minimalist%20neon%20lines%20on%20black%20background%2C%20modern%20tech%20footer%20design%20with%20soft%20neon%20lighting%2C%20ultra-detailed%20subtle%20neon%20pattern&width=1920&height=400&seq=footer-bg-neon-001&orientation=landscape"
            alt=""
            className="w-full h-full object-cover opacity-10"
          />
        </div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center">
            <div className="text-5xl font-black bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent mb-4" style={{ fontFamily: '"Orbitron", sans-serif' }}>GM</div>
            <p className="text-gray-400 mb-8">未来を創るテクノロジー</p>
            <div className="flex justify-center gap-8 mb-8 flex-wrap">
              <a href="#services" className="text-gray-400 hover:text-cyan-400 transition-colors cursor-pointer">サービス</a>
              <a href="#solutions" className="text-gray-400 hover:text-purple-400 transition-colors cursor-pointer">ソリューション</a>
              <a href="#tech" className="text-gray-400 hover:text-pink-400 transition-colors cursor-pointer">テクノロジー</a>
              <a href="#about" className="text-gray-400 hover:text-cyan-400 transition-colors cursor-pointer">会社情報</a>
              <a href="#contact" className="text-gray-400 hover:text-purple-400 transition-colors cursor-pointer">お問い合わせ</a>
              <a href="/privacy" className="text-gray-400 hover:text-pink-400 transition-colors cursor-pointer">プライバシーポリシー</a>
            </div>
            <div className="border-t border-cyan-500/20 pt-8">
              <p className="text-gray-500 text-sm">
                &copy; 2024 株式会社GM. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>

    </div>
  );
}
